import React, {useEffect, useState} from "react";
import {Calendar, dateFnsLocalizer, Event, SlotInfo, Views,} from "react-big-calendar";
import format from "date-fns/format";
import parse from "date-fns/parse";
import startOfWeek from "date-fns/startOfWeek";
import getDay from "date-fns/getDay";
import enUS from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import preloadedHolidays from "../../data/holidays.json";
import dayjs from "dayjs";
import api from "../../api";
import {Button, DatePicker, Form, message, Modal, Select} from "antd";
import {DndProvider} from "react-dnd";
import {HTML5Backend} from "react-dnd-html5-backend";
import withDragAndDrop from "react-big-calendar/lib/addons/dragAndDrop";
import "react-big-calendar/lib/addons/dragAndDrop/styles.css";

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales: {"en-US": enUS},
});

const DnDCalendar = withDragAndDrop<WorkEvent>(Calendar);
const {Option} = Select;
const {RangePicker} = DatePicker;

const RESPONSIBILITIES = ["Casino Club", "Daia", "Special Event"];

const HOLIDAY_CALENDARS = [
    {
        region: "Japan",
        id: "en.japanese#holiday@group.v.calendar.google.com",
        color: "#f5222d",
    },
    {
        region: "Taiwan",
        id: "en.taiwan#holiday@group.v.calendar.google.com",
        color: "#722ed1",
    },
    {
        region: "Hong Kong",
        id: "en.hong_kong#holiday@group.v.calendar.google.com",
        color: "#13c2c2",
    },
    {
        region: "Mainland China",
        id: "en.china#holiday@group.v.calendar.google.com",
        color: "#eb2f96",
    },
    {
        region: "Singapore",
        id: "en.singapore#holiday@group.v.calendar.google.com",
        color: "#1890ff",
    },
    {
        region: "South Korea",
        id: "ko.south_korea#holiday@group.v.calendar.google.com",
        color: "#ff4d4f",
    },
];

type WorkEvent = Event & {
    resource:
        | {
        id: number;
        user_name: string;
        responsibilities?: string[];
    }
        | {
        holidayRegion: string;
        holidayColor: string;
    };
};

const responsibilityColors: Record<string, string> = {
    "Casino Club": "#faad14",
    Daia: "#13c2c2",
    "Special Event": "#52c41a",
};

const MonthlySchedulePage: React.FC = () => {
    const [events, setEvents] = useState<WorkEvent[]>([]);
    const [users, setUsers] = useState<any[]>([]);
    const [modalVisible, setModalVisible] = useState(false);
    const [isAdmin, setIsAdmin] = useState(false);
    const [form] = Form.useForm();
    const [selectedSlot, setSelectedSlot] = useState<SlotInfo | null>(null);
    const [selectedRegions, setSelectedRegions] = useState<string[]>(["Japan"]);
    const [editingEvent, setEditingEvent] = useState<WorkEvent | null>(null);
    const [currentUserId, setCurrentUserId] = useState<number | null>(null);
    const userStr = localStorage.getItem("user");
    const user = userStr ? JSON.parse(userStr) : null;
    const firstName = user?.first_name || user?.username || "friend";


    useEffect(() => {
        checkAdmin()
        loadAllEvents();
    }, [selectedRegions]);

    const checkAdmin = async () => {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        setIsAdmin(user.is_staff || user.is_superuser);
        setCurrentUserId(user.id);
    };

    const loadAllEvents = async () => {
        try {
            const [scheduleRes, userRes, entertainmentRes] = await Promise.all([
                api.get("/users/work-schedules/", {params: {page: 1, page_size: 100}}),
                api.get("/users/", {params: {page: 1, page_size: 100}}),
                api.get("/cc/events/", {params: {page: 1, page_size: 100}}),
            ]);

            // Always safely extract results
            const extractResults = (res: any) => Array.isArray(res.data) ? res.data : res.data.results || [];

            const schedulesArray = extractResults(scheduleRes);
            const usersArray = extractResults(userRes);
            const eventsArray = extractResults(entertainmentRes);

            setUsers(usersArray);

            const validUserIds = new Set(usersArray.map((u: any) => u.id));

            const workEvents = schedulesArray
                .filter((s: any) => validUserIds.has(s.user))
                .map((s: any) => ({
                    id: `work-${s.id}`,
                    title: `${s.user_name} (${s.responsibilities?.join(", ")})`,
                    start: new Date(s.start_time),
                    end: new Date(s.end_time),
                    resource: {
                        ...s,
                        user: s.user,
                        type: "work-schedule",
                        color: "#1890ff",
                    },
                }));

            const composeDateTime = (date: string, time?: string) => {
                if (!date || !time) return dayjs(NaN);

                const parsedTime = time.includes("T") ? dayjs(time).format("HH:mm") : time;
                const [hours, minutes] = parsedTime.split(":").map(Number);

                if (isNaN(hours) || isNaN(minutes)) return dayjs(NaN);

                return dayjs(date).hour(hours).minute(minutes).second(0).millisecond(0);
            };

            const entertainmentEvents = eventsArray.flatMap((e: any, idx: number) =>
                (e.event_dates || []).map((ed: any, dateIdx: number) => {
                    const isAllDay = ed.all_day ?? false;

                    let start: dayjs.Dayjs;
                    let end: dayjs.Dayjs;

                    if (isAllDay) {
                        start = dayjs(ed.date).startOf("day");
                        end = dayjs(ed.date).endOf("day");
                    } else {
                        start = composeDateTime(ed.date, ed.start_time);
                        end = composeDateTime(ed.date, ed.end_time);
                    }

                    return {
                        id: `ent-${e.id}-${dateIdx}`,
                        title: `[Event] ${e.name}`,
                        start: start.isValid() ? start.toDate() : new Date(ed.date),
                        end: end.isValid() ? end.toDate() : new Date(ed.date),
                        allDay: isAllDay,
                        resource: {
                            type: "entertainment",
                            color: "#000000",
                            eventId: e.id,
                        },
                    };
                })
            );

            const filteredHolidays = preloadedHolidays
                .filter((h: any) =>
                    selectedRegions
                        .map((r) => r.toLowerCase().trim())
                        .includes(h.resource?.holidayRegion?.toLowerCase().trim())
                )
                .map((h: any) => {
                    const start = new Date(h.start);
                    const end = new Date(start);
                    end.setHours(23, 59, 59, 999);

                    return {
                        id: `holiday-${h.id}`,
                        title: h.title,
                        start,
                        end,
                        allDay: true,
                        resource: {
                            type: "holiday",
                            holidayRegion: h.resource?.holidayRegion,
                            holidayColor: h.resource?.holidayColor,
                        },
                    };
                });

            // Combine everything
            setEvents([
                ...workEvents,
                ...entertainmentEvents,
                ...filteredHolidays,
            ]);
        } catch (error) {
            console.error("Failed to load events:", error);
            message.error("Failed to load events. Please try again.");
        }
    };

    const handleSubmit = async (values: any) => {
        const [start, end] = values.range;
        const userId = typeof values.user === "object" ? values.user.id : values.user;

        const payload = {
            user: userId,
            start_time: start.toISOString(),
            end_time: end.toISOString(),
            responsibilities: values.responsibilities,
        };


        try {
            if (editingEvent) {
                await api.patch(`/users/work-schedules/${editingEvent.resource.id}/`, payload);
                message.success("Schedule updated");
            } else {
                await api.post("/users/work-schedules/", payload);
                message.success("Schedule added");
            }

            setModalVisible(false);
            form.resetFields();
            setEditingEvent(null);
            setSelectedSlot(null);
            loadAllEvents();
        } catch (e) {
            message.error("Failed to save schedule");
        }
    };


    const onEventDrop = async ({event, start, end}: { event: WorkEvent; start: Date | string; end: Date | string }) => {
        const userString = localStorage.getItem("user");
        let user;
        try {
            user = userString ? JSON.parse(userString) : null;
        } catch (e) {
            console.warn("⚠️ Failed to parse user from localStorage", e);
            user = null;
        }

        if (event.resource?.type === "entertainment") {
            const name = user?.first_name || "friend";
            message.warning(`Stop trying to break things, ${name}!`);
            return;
        }

        try {
            await api.patch(`/users/work-schedules/${event.resource.id}/`, {
                start_time: new Date(start).toISOString(),
                end_time: new Date(end).toISOString(),
            });
            loadAllEvents();
            message.success("Schedule updated");
        } catch {
            message.error("Failed to update schedule");
        }
    };


    const handleSelectSlot = (slotInfo: SlotInfo) => {
        setSelectedSlot(slotInfo);
        form.setFieldsValue({
            range: [dayjs(slotInfo.start), dayjs(slotInfo.end)],
        });
        setModalVisible(true);
    };

    const eventStyleGetter = (event: WorkEvent) => {
        const isHoliday = event.resource?.holidayColor;
        const isEntertainment = event.resource?.type === "entertainment";
        const backgroundColor =
            (isHoliday && event.resource.holidayColor) ||
            (isEntertainment && event.resource.color) ||
            responsibilityColors[event.resource?.responsibilities?.[0]] ||
            "#888";


        return {
            style: {
                backgroundColor,
                borderRadius: "4px",
                opacity: 0.85,
                color: "#fff",
                border: "none",
                padding: "2px",
            },
        };
    };

    return (
        <DndProvider backend={HTML5Backend}>
            <div style={{padding: 24}}>
                <div style={{display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16}}>
                    <div style={{marginLeft: 0}}>
                        <div style={{fontWeight: "bold", marginBottom: 8}}>Holiday Filter</div>
                        <Select
                            mode="multiple"
                            value={selectedRegions}
                            onChange={(val) => setSelectedRegions(val)}
                            style={{width: 400}}
                        >
                            {HOLIDAY_CALENDARS.map((calendar) => (
                                <Option key={calendar.region} value={calendar.region}>
                                    {calendar.region}
                                </Option>
                            ))}
                        </Select>
                    </div>
                    {
                        isAdmin &&
                        <Button type="primary" onClick={() => setModalVisible(true)}>
                            Add Schedule
                        </Button>
                    }
                </div>

                <DnDCalendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    style={{height: 700}}
                    views={[Views.MONTH, Views.WEEK]}
                    defaultView={Views.MONTH}
                    selectable={isAdmin}
                    onSelectSlot={isAdmin ? handleSelectSlot : undefined}
                    onEventDrop={isAdmin ? onEventDrop : undefined}
                    onSelectEvent={(event) => {
                        if (isAdmin && !event.resource?.type && !event.resource?.holidayRegion) {
                            setEditingEvent(event);
                            form.setFieldsValue({
                                user: event.resource.user,
                                responsibilities: event.resource.responsibilities,
                                range: [dayjs(event.start), dayjs(event.end)],
                            });
                            setModalVisible(true);
                        }
                    }}
                    eventPropGetter={eventStyleGetter}
                    resizable={isAdmin}
                    dayPropGetter={(date) => {
                        const isPast = dayjs(date).isBefore(dayjs(), 'day');

                        return isPast
                            ? {
                                style: {
                                    backgroundColor: '#f0f0f0', // light grey
                                },
                            }
                            : {};
                    }}
                />

                <Modal
                    open={modalVisible}
                    title="Add Schedule"
                    onCancel={() => {
                        setModalVisible(false);
                        setSelectedSlot(null);
                        setEditingEvent(null);
                    }}
                    onOk={() => form.submit()}
                    footer={[
                        <Button key="cancel" onClick={() => {
                            setModalVisible(false);
                            setEditingEvent(null);
                        }}>
                            Cancel
                        </Button>,

                        isAdmin && editingEvent && (
                            <Button
                                key="delete"
                                danger
                                onClick={async () => {
                                    try {
                                        await api.delete(`/users/work-schedules/${editingEvent.resource.id}/`);
                                        message.success("Schedule deleted");
                                        setModalVisible(false);
                                        setEditingEvent(null);
                                        loadAllEvents();
                                    } catch {
                                        message.error("Failed to delete schedule");
                                    }
                                }}
                            >
                                Delete
                            </Button>
                        ),

                        isAdmin && (
                            <Button key="submit" type="primary" onClick={() => form.submit()}>
                                {editingEvent ? "Update" : "Add"}
                            </Button>
                        )
                    ]}
                >
                    <Form layout="vertical" form={form} onFinish={handleSubmit}>
                        <Form.Item name="user" label="User" rules={[{required: true}]}>
                            <Select>
                                {users.map((u: any) => (
                                    <Option key={u.id} value={u.id}>
                                        {u.first_name} {u.last_name}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                        <Form.Item name="range" label="Schedule Range" rules={[{required: true}]}>
                            <RangePicker
                                showTime={{format: "HH:mm", defaultValue: [dayjs(), dayjs()]}}
                                format="YYYY-MM-DD HH:mm"
                                style={{width: "100%"}}
                            />
                        </Form.Item>
                        <Form.Item name="responsibilities" label="Responsibilities">
                            <Select mode="multiple">
                                {RESPONSIBILITIES.map((r) => (
                                    <Option key={r} value={r}>{r}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </DndProvider>
    );
};

export default MonthlySchedulePage;
